import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, PlayCircle, Clock } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import { meApi } from "@/lib/api";
import { formatDuration } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function DashboardPage() {
    const { user } = useAuth();
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        meApi
            .enrollments()
            .then(setEnrollments)
            .catch(() => setEnrollments([]))
            .finally(() => setLoading(false));
    }, []);

    const totalCourses = enrollments.length;
    const totalLessons = enrollments.reduce((s, e) => s + e.total_lessons, 0);
    const completedLessons = enrollments.reduce(
        (s, e) => s + e.completed_lesson_count,
        0,
    );

    return (
        <div className="bg-brand-offwhite" data-testid="dashboard-page">
            <section className="border-b border-brand-line py-20 md:py-28">
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={0} label="Dashboard" />
                        <h1 className="mt-6 font-display text-6xl leading-[1.05] md:text-8xl">
                            {(user?.full_name || "TRAINEE").split(" ")[0].toUpperCase()}, <br />
                            <span className="text-brand-gold">KEEP GOING.</span>
                        </h1>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 gap-6 border-t border-l border-brand-line md:grid-cols-3">
                        {[
                            { k: totalCourses, v: "Enrolled Courses" },
                            { k: completedLessons, v: "Lessons Completed" },
                            { k: totalLessons - completedLessons, v: "Lessons Remaining" },
                        ].map((s, i) => (
                            <Reveal
                                key={s.v}
                                delay={i * 0.08}
                                className="border-b border-r border-brand-line bg-white p-8"
                            >
                                <div data-testid={`dashboard-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <p className="font-display text-6xl text-brand-gold">
                                        {s.k}
                                    </p>
                                    <p className="mt-2 font-mono-tag text-brand-steel">
                                        {s.v}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-24">
                <div className="container-x">
                    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <div>
                            <Reveal>
                                <SectionLabel index={1} label="Your courses" />
                                <h2 className="mt-6 font-display text-4xl leading-[1.1] md:text-5xl">
                                    CONTINUE <span className="text-brand-gold">LEARNING.</span>
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <Link
                                to="/portal/courses"
                                className="btn-secondary"
                                data-testid="dashboard-browse-more"
                            >
                                Browse catalog
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-12">
                        {loading ? (
                            <p
                                className="font-mono-tag text-brand-steel"
                                data-testid="dashboard-loading"
                            >
                                LOADING…
                            </p>
                        ) : enrollments.length === 0 ? (
                            <div
                                className="border border-dashed border-brand-line bg-white p-12 text-center"
                                data-testid="dashboard-empty-state"
                            >
                                <BookOpen className="mx-auto h-10 w-10 text-brand-gold" />
                                <p className="mt-6 font-display text-3xl uppercase">
                                    No enrollments yet
                                </p>
                                <p className="mt-3 font-body text-brand-steel">
                                    Pick a course and start progressing.
                                </p>
                                <Link
                                    to="/portal/courses"
                                    className="mt-8 inline-flex btn-primary"
                                    data-testid="dashboard-empty-cta"
                                >
                                    Browse courses
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                {enrollments.map((e, i) => (
                                    <Reveal
                                        key={e.course.id}
                                        delay={i * 0.05}
                                        className="group border border-brand-line bg-white"
                                    >
                                        <div
                                            className="grid grid-cols-1 sm:grid-cols-[160px_1fr]"
                                            data-testid={`dashboard-enrollment-${e.course.slug}`}
                                        >
                                            <div className="aspect-[4/3] overflow-hidden sm:aspect-auto">
                                                <img
                                                    src={e.course.cover_image}
                                                    alt={e.course.title}
                                                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                                />
                                            </div>
                                            <div className="flex flex-col justify-between p-6">
                                                <div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="border border-brand-gold px-2 py-1 font-mono-tag text-brand-gold">
                                                            {e.course.category}
                                                        </span>
                                                        <span className="font-mono-tag text-brand-steel">
                                                            {e.course.level}
                                                        </span>
                                                    </div>
                                                    <h3 className="mt-3 font-display text-2xl uppercase leading-tight">
                                                        {e.course.title}
                                                    </h3>
                                                </div>

                                                <div className="mt-4">
                                                    <div className="flex items-center justify-between font-mono-tag text-brand-steel">
                                                        <span data-testid={`dashboard-progress-${e.course.slug}`}>
                                                            {e.progress_percent}% ·{" "}
                                                            {e.completed_lesson_count}/{e.total_lessons}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="h-3 w-3" />
                                                            {formatDuration(e.course.duration_minutes)}
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 h-1.5 w-full bg-brand-line">
                                                        <div
                                                            className="h-full bg-brand-gold transition-all"
                                                            style={{
                                                                width: `${e.progress_percent}%`,
                                                            }}
                                                        />
                                                    </div>
                                                    {e.next_lesson_id ? (
                                                        <Link
                                                            to={`/portal/learn/${e.course.slug}/${e.next_lesson_id}`}
                                                            className="mt-6 inline-flex items-center gap-2 border border-brand-charcoal px-4 py-2 font-mono-tag text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-brand-gold"
                                                            data-testid={`dashboard-continue-${e.course.slug}`}
                                                        >
                                                            <PlayCircle className="h-3.5 w-3.5" />
                                                            Continue
                                                        </Link>
                                                    ) : (
                                                        <p className="mt-6 font-mono-tag text-brand-gold">
                                                            ✓ COURSE COMPLETE
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
